/**
 * @fileoverview Admin RBAC Dashboard — Access Management
 * @module features/AdminRBAC/pages/AdminRBACPage
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Tooltip,
  InputAdornment,
  Snackbar,
  Switch,
  Fade,
  Collapse,
  Avatar,
  Stack,
  alpha,
} from '@mui/material';
import {
  Shield,
  ShieldCheck,
  UserPlus,
  Check,
  X,
  Search,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  KeyRound,
  LayoutGrid,
  Inbox,
  Save,
  User,
  ChevronRight,
} from 'lucide-react';
import Spinner from '@/components/common/Spinner/Spinner';
import {
  fetchAccessRequests,
  reviewAccessRequest,
  fetchRoles,
  fetchModules,
  fetchRolePermissions,
  setRolePermissions,
  fetchModulePermissions,
  fetchUserRoles,
  assignUserRole,
  revokeUserRole,
  fetchUserModules,
  grantUserModule,
  revokeUserModule,
} from '@/services/api/rbac';
import { appInsights } from '@/telemetry';
import { Base64 } from 'js-base64'; 

// ─── Shared Styles ──────────────────────────────────
const NAVY = '#01426A';
const TEAL = '#0097A9';
const BORDER = '#E5E7EB';
const BG_SUBTLE = '#F8FAFC';

const cardSx = {
  background: '#fff',
  borderRadius: '12px',
  border: `1px solid ${BORDER}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

const headerGradient = {
  background: `linear-gradient(135deg, ${NAVY} 0%, ${TEAL} 100%)`,
  color: '#fff',
  px: 3,
  py: 2.5,
};

const sectionCard = {
  ...cardSx,
  mb: 0,
};

const tableHeadSx = {
  '& .MuiTableCell-head': {
    background: BG_SUBTLE,
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#6B7280',
    borderBottom: `2px solid ${BORDER}`,
    py: 1.5,
  },
};

const tableRowHover = {
  '&:hover': { backgroundColor: '#F0F9FF' },
  '&:last-child td': { borderBottom: 0 },
};

// ─── Shared Components ──────────────────────────────

const StatusPill = ({ status }) => {
  const cfg = {
    pending: { bg: '#FFF7ED', color: '#C2410C', icon: Clock, label: 'Pending' },
    approved: { bg: '#F0FDF4', color: '#15803D', icon: CheckCircle, label: 'Approved' },
    rejected: { bg: '#FEF2F2', color: '#B91C1C', icon: XCircle, label: 'Rejected' },
  }[status] || { bg: '#F3F4F6', color: '#6B7280', icon: Clock, label: status };

  const Icon = cfg.icon;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.5, borderRadius: '20px',
      backgroundColor: cfg.bg, color: cfg.color,
      fontSize: '0.75rem', fontWeight: 600,
    }}>
      <Icon size={12} />
      {cfg.label}
    </Box>
  );
};

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Box sx={{
      width: 56, height: 56, borderRadius: '14px',
      background: `linear-gradient(135deg, ${alpha(NAVY, 0.08)}, ${alpha(TEAL, 0.08)})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      mx: 'auto', mb: 2,
    }}>
      <Icon size={24} color={NAVY} />
    </Box>
    <Typography fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
  </Box>
);

const UserSearchBar = ({ userId, setUserId, onSearch, loading, placeholder = 'e.g. v-smondal1' }) => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 0 }}>
    <TextField
      placeholder={placeholder}
      value={userId}
      onChange={(e) => setUserId(e.target.value)}
      size="small"
      fullWidth
      onKeyDown={(e) => e.key === 'Enter' && onSearch()}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '10px',
          backgroundColor: '#fff',
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start"><Search size={16} color="#9CA3AF" /></InputAdornment>
        ),
      }}
    />
    <Button
      variant="contained"
      onClick={onSearch}
      disabled={loading || !userId.trim()}
      disableElevation
      sx={{
        borderRadius: '10px',
        textTransform: 'none',
        fontWeight: 600,
        px: 3,
        color: '#fff',
        backgroundColor: NAVY,
        '&:hover': { backgroundColor: '#002F5F' },
        '&.Mui-disabled': { backgroundColor: '#D1D5DB', color: '#9CA3AF' },
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
    </Button>
  </Box>
);

// ─── Tab Navigation ─────────────────────────────────

const tabs = [
  { icon: Inbox, label: 'Requests', key: 'requests' },
  { icon: Users, label: 'User Roles', key: 'roles' },
  { icon: KeyRound, label: 'Permissions', key: 'permissions' },
  { icon: LayoutGrid, label: 'Modules', key: 'modules' },
];

const TabNav = ({ activeTab, onTabChange }) => (
  <Box sx={{
    display: 'flex', gap: 0.5, p: 0.5,
    borderRadius: '12px', background: '#F3F4F6',
  }}>
    {tabs.map((t, i) => {
      const Icon = t.icon;
      const active = activeTab === i;
      return (
        <Box
          key={t.key}
          onClick={() => onTabChange(i)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 1, borderRadius: '10px',
            cursor: 'pointer', transition: 'all 0.2s ease',
            fontWeight: active ? 600 : 500,
            fontSize: '0.875rem',
            color: active ? '#fff' : '#6B7280',
            background: active ? `linear-gradient(135deg, ${NAVY}, ${TEAL})` : 'transparent',
            boxShadow: active ? '0 2px 8px rgba(1,66,106,0.25)' : 'none',
            '&:hover': active ? {} : { background: '#E5E7EB', color: '#374151' },
            userSelect: 'none',
          }}
        >
          <Icon size={16} />
          {t.label}
        </Box>
      );
    })}
  </Box>
);

// ─── Main Admin Page ────────────────────────────────

const AdminRBACPage = () => {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '12px',
            background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShieldCheck size={22} color="#fff" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color={NAVY} sx={{ lineHeight: 1.2 }}>
              Access Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage user roles, permissions, and module access
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tab Navigation */}
      <Box sx={{ mb: 3 }}>
        <TabNav activeTab={tab} onTabChange={setTab} />
      </Box>

      {/* Tab Content */}
      <Fade in key={tab} timeout={200}>
        <Box>
          {tab === 0 && <AccessRequestsTab showToast={showToast} />}
          {tab === 1 && <UserRolesTab showToast={showToast} />}
          {tab === 2 && <RolePermissionsTab showToast={showToast} />}
          {tab === 3 && <ModuleAccessTab showToast={showToast} />}
        </Box>
      </Fade>

      {/* Global Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ borderRadius: '10px', fontWeight: 500 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════
//  TAB 1: Access Requests
// ═══════════════════════════════════════════════════════

const AccessRequestsTab = ({ showToast }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAccessRequests(statusFilter || undefined);
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      showToast('error', 'Failed to load requests');
      appInsights.trackException({ name: 'FetchAccessRequestsError', message: 'Failed to load access requests' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (id, status) => {
    const notes = status === 'rejected' ? prompt('Rejection reason (optional):') : '';
    try {
      setActionLoading(id);
      await reviewAccessRequest(id, status, notes || undefined);
      showToast('success', `Request ${status}`);
      load();

      // Telemetry: Track request review with status and optional notes
      appInsights.trackEvent({
        name: 'ReviewAccessRequest',
        properties: {
          requestId: Base64.encode(id),
          status: status,
        }
      });
    } catch (err) {
      showToast('error', err.message || `Failed to ${status} request`);
      appInsights.trackException({ name: 'ReviewAccessRequestError', message: err.message || `Failed to ${status} request` });
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <Box sx={sectionCard}>
      {/* Section Header */}
      <Box sx={{
        px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${BORDER}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color={NAVY}>
            Access Requests
          </Typography>
          {pendingCount > 0 && (
            <Chip
              label={`${pendingCount} pending`}
              size="small"
              sx={{
                fontWeight: 600, fontSize: '0.7rem', height: 22,
                backgroundColor: '#FFF7ED', color: '#C2410C',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            select size="small" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{
              minWidth: 130,
              '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem' },
            }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small" sx={{ color: '#6B7280' }}>
              <RefreshCw size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
        ) : requests.length === 0 ? (
          <EmptyState icon={Inbox} title="No requests found" subtitle="Access requests from users will appear here" />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={tableHeadSx}>
                  <TableCell>User</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req) => (
                  <TableRow key={req.id} sx={tableRowHover}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{
                          width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700,
                          background: `linear-gradient(135deg, ${alpha(NAVY, 0.1)}, ${alpha(TEAL, 0.1)})`,
                          color: NAVY,
                        }}>
                          {(req.user_name || req.user_id || '?')[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                            {req.user_name || req.user_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {req.user_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{req.module_display_name || req.module_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{req.requested_role_display_name || req.requested_role_name || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={req.reason}>
                        {req.reason || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><StatusPill status={req.status} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {req.status === 'pending' && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              onClick={() => handleReview(req.id, 'approved')}
                              disabled={actionLoading === req.id}
                              sx={{
                                backgroundColor: '#F0FDF4', color: '#15803D',
                                '&:hover': { backgroundColor: '#DCFCE7' },
                              }}
                            >
                              {actionLoading === req.id ? <CircularProgress size={14} /> : <Check size={14} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              onClick={() => handleReview(req.id, 'rejected')}
                              disabled={actionLoading === req.id}
                              sx={{
                                backgroundColor: '#FEF2F2', color: '#B91C1C',
                                '&:hover': { backgroundColor: '#FEE2E2' },
                              }}
                            >
                              <X size={14} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════
//  TAB 2: User Roles
// ═══════════════════════════════════════════════════════

const UserRolesTab = ({ showToast }) => {
  const [userId, setUserId] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [userRolesData, setUserRolesData] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    fetchRoles().then((data) => setAllRoles(Array.isArray(data) ? data : []));
  }, []);

  const searchUser = async () => {
    if (!userId.trim()) return;
    try {
      setLoading(true);
      setSearchUserId(userId.trim());
      const data = await fetchUserRoles(userId.trim());
      setUserRolesData(Array.isArray(data) ? data : []);

      // Telemetry: Track role fetch with user ID and role count
      appInsights.trackEvent({
          name: 'FetchUserAndRoles',
          properties: { 
            roles: Array.isArray(data) ? data.join(',') : 'None',
            userId: Base64.encode(userId.trim())   
          }
        });
      
    } catch (err) {
      showToast('error', 'Failed to fetch user roles');
      appInsights.trackException({
        exception: err instanceof Error
          ? err
          : new Error(`Failed to fetch roles for user ${Base64.encode(userId.trim())}`),
        properties: {
          name: 'FetchUserRolesError',
          userId: Base64.encode(userId.trim()),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedRole || !searchUserId) return;
    try {
      const data = await assignUserRole(searchUserId, parseInt(selectedRole));
      setUserRolesData(Array.isArray(data) ? data : []);
      setSelectedRole('');
      showToast('success', 'Role assigned successfully');
      // Telemetry: Track role assignment with user ID and assigned role
      appInsights.trackEvent({
        name: 'AssignUserRole',
        properties: {
          userId: Base64.encode(searchUserId),
          roleId: selectedRole,
        },
      });
    } catch (err) {
      showToast('error', err.message || 'Failed to assign role');
      appInsights.trackException({ error: err });
    }
  };

  const handleRevoke = async (roleId) => {
    try {
      await revokeUserRole(searchUserId, roleId);
      const data = await fetchUserRoles(searchUserId);
      setUserRolesData(Array.isArray(data) ? data : []);
      showToast('success', 'Role revoked');

      // Telemetry: Track role revocation with user ID and revoked role
      appInsights.trackEvent({
        name: 'RevokeUserRole',
        properties: {
          userId: Base64.encode(searchUserId),
          roleId: roleId,
        }
      });

    } catch (err) {
      showToast('error', err.message || 'Failed to revoke role');
      appInsights.trackException({ error: err });
    }
  };

  const roleColors = {
    admin: { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    lead: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
    viewer: { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  };

  return (
    <Box sx={sectionCard}>
      {/* Header with search */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 0.5 }}>
          User Roles
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search for a user by their ID to view and manage role assignments.
        </Typography>
        <UserSearchBar
          userId={userId}
          setUserId={setUserId}
          onSearch={searchUser}
          loading={loading}
          placeholder="Enter user ID (e.g. v-smondal1)"
        />
      </Box>

      {/* Results */}
      <Collapse in={!!searchUserId}>
        <Box sx={{ p: 3 }}>
          {/* User Identity Card */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2, mb: 3,
            p: 2, borderRadius: '10px', backgroundColor: BG_SUBTLE,
            border: `1px solid ${BORDER}`,
          }}>
            <Avatar sx={{
              width: 44, height: 44, fontWeight: 700,
              background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
            }}>
              {searchUserId[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={600} color={NAVY}>{searchUserId}</Typography>
              <Typography variant="body2" color="text.secondary">
                {userRolesData.length} role{userRolesData.length !== 1 ? 's' : ''} assigned
              </Typography>
            </Box>
          </Box>

          {/* Current Roles */}
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
            Assigned Roles
          </Typography>

          {userRolesData.length === 0 ? (
            <Box sx={{
              p: 2, borderRadius: '10px', backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A', mb: 3,
            }}>
              <Typography variant="body2" color="#92400E" fontWeight={500}>
                No roles assigned. Assign a role below to grant permissions.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {userRolesData.map((ur) => {
                const colors = roleColors[ur.role_name] || { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' };
                return (
                  <Chip
                    key={ur.role_id}
                    label={ur.role_display_name || ur.role_name}
                    onDelete={() => handleRevoke(ur.role_id)}
                    deleteIcon={<X size={14} />}
                    sx={{
                      fontWeight: 600, fontSize: '0.8rem',
                      backgroundColor: colors.bg,
                      color: colors.color,
                      border: `1px solid ${colors.border}`,
                      '& .MuiChip-deleteIcon': { color: colors.color, '&:hover': { color: '#B91C1C' } },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Assign New Role */}
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
            Add Role
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              select size="small"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
              }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled><em>Choose a role…</em></MenuItem>
              {allRoles
                .filter((r) => !userRolesData.some((ur) => ur.role_id === r.id))
                .map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.display_name}</MenuItem>
                ))}
            </TextField>
            <Button
              variant="contained"
              disableElevation
              startIcon={<UserPlus size={16} />}
              onClick={handleAssign}
              disabled={!selectedRole}
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                color: '#fff', backgroundColor: NAVY,
                '&:hover': { backgroundColor: '#002F5F' },
                '&.Mui-disabled': { backgroundColor: '#D1D5DB', color: '#9CA3AF' },
              }}
            >
              Assign
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Empty state when no search yet */}
      {!searchUserId && (
        <EmptyState icon={Users} title="Search for a user" subtitle="Enter a user ID above to manage their roles" />
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════
//  TAB 3: Role Permissions
// ═══════════════════════════════════════════════════════

const RolePermissionsTab = ({ showToast }) => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [allPerms, setAllPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [rolesData, modulesData] = await Promise.all([fetchRoles(), fetchModules()]);
        setRoles(Array.isArray(rolesData) ? rolesData : []);
        setModules(Array.isArray(modulesData) ? modulesData : []);
        const permsMap = {};
        for (const m of (Array.isArray(modulesData) ? modulesData : [])) {
          const perms = await fetchModulePermissions(m.id);
          permsMap[m.id] = Array.isArray(perms) ? perms : [];
        }
        setAllPerms(permsMap);
      } catch (error) {
        showToast('error', 'Failed to load data');
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Failed to load roles or modules'),
          properties: {
            name: 'LoadRolesModulesError',
            message: 'Failed to load roles or modules',
          },
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadRolePerms = async (roleId) => {
    setSelectedRoleId(roleId);
    if (!roleId) { setCheckedIds(new Set()); setOriginalIds(new Set()); return; }
    try {
      const data = await fetchRolePermissions(roleId);
      const perms = Array.isArray(data) ? data : [];
      const ids = new Set(perms.map((p) => p.id));
      setCheckedIds(ids);
      setOriginalIds(new Set(ids));
    } catch (error) {
      showToast('error', 'Failed to load role permissions');
      const exception = error instanceof Error ? error : new Error('Failed to fetch role permissions');
      appInsights.trackException({
        exception,
        properties: {
          name: 'FetchRolePermissionsError',
          roleId: Base64.encode(String(roleId)),
        },
      });
    }
  };

  const togglePerm = (permId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(permId) ? next.delete(permId) : next.add(permId);
      return next;
    });
  };

  const hasChanges = () => {
    if (checkedIds.size !== originalIds.size) return true;
    for (const id of checkedIds) if (!originalIds.has(id)) return true;
    return false;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await setRolePermissions(parseInt(selectedRoleId), Array.from(checkedIds));
      setOriginalIds(new Set(checkedIds));
      showToast('success', 'Permissions updated successfully');

      // Telemetry: Track permission updates with role ID and number of permissions
      appInsights.trackEvent({
        name: 'UpdateRolePermissions',
        properties: {
          roleId: selectedRoleId,
          permissionsCount: checkedIds.size,
        }
      });
    } catch (err) {
      showToast('error', err.message || 'Failed to update permissions');
      appInsights.trackException({
        exception: err,
        properties: {
          eventName: 'UpdateRolePermissionsError',
          roleId: Base64.encode(String(selectedRoleId)),
          message: 'Failed to update permissions',
        },
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>;
  }

  const selectedRole = roles.find((r) => r.id == selectedRoleId);

  return (
    <Box sx={sectionCard}>
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 0.5 }}>
          Role Permissions Matrix
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define what each role can do across every module.
        </Typography>

        {/* Role Selector as Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {roles.map((r) => {
            const active = selectedRoleId == r.id;
            return (
              <Chip
                key={r.id}
                label={r.display_name}
                onClick={() => loadRolePerms(r.id)}
                icon={<Shield size={14} />}
                sx={{
                  fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                  borderRadius: '8px', transition: 'all 0.2s',
                  ...(active
                    ? {
                        background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                        color: '#fff',
                        '& .MuiChip-icon': { color: '#fff' },
                      }
                    : {
                        backgroundColor: '#F3F4F6', color: '#374151',
                        border: `1px solid ${BORDER}`,
                        '&:hover': { backgroundColor: '#E5E7EB' },
                        '& .MuiChip-icon': { color: '#6B7280' },
                      }),
                }}
              />
            );
          })}
        </Box>
      </Box>

      {selectedRoleId ? (
        <Box sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={tableHeadSx}>
                  <TableCell>Module</TableCell>
                  {['Read', 'Write', 'Delete', 'Manage'].map((op) => (
                    <TableCell key={op} align="center" sx={{ width: 100 }}>{op}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((m) => {
                  const perms = allPerms[m.id] || [];
                  return (
                    <TableRow key={m.id} sx={tableRowHover}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="#1F2937">
                          {m.display_name}
                        </Typography>
                      </TableCell>
                      {['read', 'write', 'delete', 'manage'].map((op) => {
                        const perm = perms.find((p) => p.operation === op);
                        if (!perm) return <TableCell key={op} align="center"><Typography color="#D1D5DB">—</Typography></TableCell>;
                        const checked = checkedIds.has(perm.id);
                        return (
                          <TableCell key={op} align="center">
                            <Checkbox
                              checked={checked}
                              onChange={() => togglePerm(perm.id)}
                              size="small"
                              sx={{
                                color: '#D1D5DB',
                                '&.Mui-checked': { color: TEAL },
                              }}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Save Bar */}
          <Collapse in={hasChanges()}>
            <Box sx={{
              px: 3, py: 2,
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2,
              borderTop: `1px solid ${BORDER}`,
              backgroundColor: BG_SUBTLE,
            }}>
              <Typography variant="body2" color="text.secondary">
                Unsaved changes for <strong>{selectedRole?.display_name}</strong>
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
                sx={{
                  borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                  background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                  '&:hover': { background: `linear-gradient(135deg, #002F5F, #007A8A)` },
                }}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </Box>
          </Collapse>
        </Box>
      ) : (
        <EmptyState icon={KeyRound} title="Select a role" subtitle="Choose a role above to configure its permissions" />
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════
//  TAB 4: Module Access
// ═══════════════════════════════════════════════════════

const ModuleAccessTab = ({ showToast }) => {
  const [userId, setUserId] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [modules, setModules] = useState([]);
  const [userModules, setUserModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchModules().then((data) => setModules(Array.isArray(data) ? data.filter((m) => m.name !== 'admin_rbac') : []));
  }, []);

  const searchUser = async () => {
    if (!userId.trim()) return;
    try {
      setLoading(true);
      setSearchUserId(userId.trim());
      const data = await fetchUserModules(userId.trim());
      setUserModules(Array.isArray(data) ? data : []);
      // Telemetry: Track module access fetch with user ID and module count
      appInsights.trackEvent({
        name: 'FetchUserModules',
        properties: {
          userId: Base64.encode(userId.trim()),
          modulesCount: Array.isArray(data) ? data.length : 0,
        }
      });
    } catch (error) {
      showToast('error', 'Failed to fetch user module access');
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(`Failed to fetch modules for user ${Base64.encode(userId.trim())}`),
        properties: {
          name: 'FetchUserModulesError',
          message: `Failed to fetch modules for user ${Base64.encode(userId.trim())}`,
          userId: Base64.encode(userId.trim()),
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const isGranted = (moduleId) => userModules.some((um) => um.module_id === moduleId);

  const handleToggle = async (moduleId) => {
    if (!searchUserId) return;
    const wasGranted = isGranted(moduleId);
    try {
      setToggling(moduleId);
      if (wasGranted) {
        await revokeUserModule(searchUserId, moduleId);
      } else {
        await grantUserModule(searchUserId, moduleId);
      }
      const data = await fetchUserModules(searchUserId);
      setUserModules(Array.isArray(data) ? data : []);
      showToast('success', wasGranted ? 'Access revoked' : 'Access granted');
      
      // Telemetry: Track module access changes with user ID, module ID, and action
      appInsights.trackEvent({
        name: 'ToggleModuleAccess',
        properties: {
          userId: Base64.encode(searchUserId),
          moduleId: Base64.encode(String(moduleId)),
          action: wasGranted ? 'revoke' : 'grant',
        }
      });
    } catch (err) {
      showToast('error', err.message || 'Failed to update module access');
      appInsights.trackException({ error: err });
    } finally {
      setToggling(null);
    }
  };

  const grantedCount = modules.filter((m) => isGranted(m.id)).length;

  return (
    <Box sx={sectionCard}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 0.5 }}>
          Module Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Toggle which modules a user can access. Only granted modules appear in their sidebar.
        </Typography>
        <UserSearchBar
          userId={userId}
          setUserId={setUserId}
          onSearch={searchUser}
          loading={loading}
          placeholder="Enter user ID (e.g. v-smondal1)"
        />
      </Box>

      {/* Results */}
      <Collapse in={!!searchUserId && !loading}>
        <Box sx={{ p: 3 }}>
          {/* User card with stats */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 2, borderRadius: '10px', backgroundColor: BG_SUBTLE,
            border: `1px solid ${BORDER}`, mb: 3,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{
                width: 44, height: 44, fontWeight: 700,
                background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
              }}>
                {searchUserId[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography fontWeight={600} color={NAVY}>{searchUserId}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {grantedCount} of {modules.length} modules enabled
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.5, py: 0.5, borderRadius: '20px',
              backgroundColor: grantedCount > 0 ? '#F0FDF4' : '#FEF2F2',
              color: grantedCount > 0 ? '#15803D' : '#B91C1C',
              fontWeight: 600, fontSize: '0.75rem',
            }}>
              {grantedCount > 0 ? <CheckCircle size={12} /> : <XCircle size={12} />}
              {grantedCount > 0 ? 'Active' : 'No access'}
            </Box>
          </Box>

          {/* Module Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 1.5,
          }}>
            {modules.map((m) => {
              const granted = isGranted(m.id);
              const isToggling = toggling === m.id;
              return (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 2, borderRadius: '10px',
                    border: `1px solid ${granted ? '#BBF7D0' : BORDER}`,
                    backgroundColor: granted ? '#FAFFF9' : '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: granted ? '#86EFAC' : '#D1D5DB' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} color="#1F2937">
                      {m.display_name}
                    </Typography>
                    {m.description && (
                      <Typography variant="caption" color="text.secondary" noWrap title={m.description}>
                        {m.description}
                      </Typography>
                    )}
                  </Box>
                  {isToggling ? (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  ) : (
                    <Switch
                      checked={granted}
                      onChange={() => handleToggle(m.id)}
                      size="small"
                      sx={{
                        ml: 1,
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#15803D' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#86EFAC' },
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>

      {/* Empty state */}
      {!searchUserId && !loading && (
        <EmptyState icon={LayoutGrid} title="Search for a user" subtitle="Enter a user ID above to manage their module access" />
      )}
    </Box>
  );
};

export default AdminRBACPage;
